import useUserAuth from "../../../../auth/hook/useUserAuth";
import useSpendingDemandFilter from "../../../hooks/useSpendingDemandFilter";
import { FilterSpendingDemand, SpendingDemandTypes } from "../../../types/spendingDemand.types";
import SpendingDemandListFilterView, {
  StatusItem,
  TypeItem,
} from "./SpendingDemandListFilterView/SpendingDemandListFilterView";

import { useState, useEffect, ChangeEvent } from "react";
import { Button, Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";

import SearchPickProject from "../../../../../features/project/components/SearchPickProject";
import SearchPickUser from "../../../../../features/user/components/SearchPickUser";
import { useBoxAssociationFromBoxId, useSingleBoxAssociation } from "../../../../boxAssociation/hooks/useBoxAssociation";

export default function SpendingDemandListFilterPresenter({ forUser = false }: { forUser?: boolean } = {}) {
  const { filter, setFilter } = useSpendingDemandFilter();
  const { userInfo } = useUserAuth();

  // États locaux pour les filtres de date et montant
  const [dateRange, setDateRange] = useState<{ min?: string; max?: string }>({
    min: filter?.date?.min || '',
    max: filter?.date?.max || '',
  });

  const [amountRange, setAmountRange] = useState<{ min?: number | ''; max?: number | '' }>({
    min: filter?.amountTTC?.min || '',
    max: filter?.amountTTC?.max || '',
  });

  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
    filter?.projectId && filter.projectId.length > 0 ? filter.projectId[0] : null
  );

  const [selectedProjectName, setSelectedProjectName] = useState<string | null>(null);

  const [selectedUserId, setSelectedUserId] = useState<string | null>(
    filter?.userId && filter.userId.length > 0 ? filter.userId[0] : null
  );

  const [selectedUserName, setSelectedUserName] = useState<string | null>(null);

  // État pour le BoxId (saisie utilisateur)
  const [boxIdInput, setBoxIdInput] = useState<string>('');
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // État pour le Readable ID
  const [readableIdInput, setReadableIdInput] = useState<string>(filter?.readableId || '');

  useEffect(() => {
    setReadableIdInput(filter?.readableId || '');
  }, [filter?.readableId]);

  // État pour la recherche plein-texte Description (param descSearch)
  const [descriptionInput, setDescriptionInput] = useState<string>(filter?.description ?? '');

  // Synchro si le filtre Redux change (ex. reset externe)
  useEffect(() => {
    setDescriptionInput(filter?.description ?? '');
  }, [filter?.description]);

  // Debounce (~400ms) : pousse la Description dans le filtre Redux.
  // `filter` est dans les deps pour éviter d'écraser un autre filtre modifié
  // pendant la fenêtre de debounce (closure périmée).
  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmed = descriptionInput.trim();
      const nextValue = trimmed !== '' ? trimmed : undefined;
      if ((filter?.description ?? undefined) !== nextValue) {
        setFilter({ ...filter, description: nextValue });
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [descriptionInput, filter, setFilter]);

  const handleDescriptionChange = (e: ChangeEvent<HTMLInputElement>) => {
    setDescriptionInput(e.target.value);
  };

  // Récupérer le boxId à partir du boxAssociationId si un filtre existe déjà
  const existingBoxAssociationIdFromFilter = filter?.boxAssociationId && filter.boxAssociationId.length > 0
    ? filter.boxAssociationId[0]
    : undefined;
  
  const {
    boxAssociation: existingBoxAssociationFromFilter,
  } = useSingleBoxAssociation(existingBoxAssociationIdFromFilter, { disableQuery: !existingBoxAssociationIdFromFilter });
  
  // État pour le debounce du BoxId
  const [debouncedBoxId, setDebouncedBoxId] = useState<string | undefined>(undefined);
  
  // Récupérer le boxAssociationId à partir du boxId
  const {
    boxAssociation: boxAssociationFromBoxId,
    isLoading: isValidatingBox,
    isError: isBoxError,
  } = useBoxAssociationFromBoxId(debouncedBoxId, { disableQuery: !debouncedBoxId });
  
  // Debounce du BoxId
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedBoxId(boxIdInput && boxIdInput.trim() !== '' ? boxIdInput.trim() : undefined);
    }, 500);

    return () => clearTimeout(timer);
  }, [boxIdInput]);
  
  // Mettre à jour le filtre quand boxAssociationId est récupéré
  useEffect(() => {
    if (boxAssociationFromBoxId?.id) {
      // Vérifier si le boxAssociationId a changé pour éviter les boucles infinies
      const currentBoxAssociationId = filter?.boxAssociationId?.[0];
      if (currentBoxAssociationId !== boxAssociationFromBoxId.id) {
        setFilter({
          ...filter,
          boxAssociationId: [boxAssociationFromBoxId.id],
        });
      }
    } else if (debouncedBoxId === undefined && !boxIdInput && filter?.boxAssociationId) {
      // Si le champ est vide et qu'il y avait un filtre, le retirer
      setFilter({
        ...filter,
        boxAssociationId: undefined,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boxAssociationFromBoxId?.id, debouncedBoxId, boxIdInput]);
  
  // Mettre à jour boxIdInput quand on récupère le boxId depuis le boxAssociationId existant (seulement au chargement initial)
  useEffect(() => {
    if (isInitialLoad && existingBoxAssociationFromFilter?.boxId) {
      setBoxIdInput(existingBoxAssociationFromFilter.boxId);
      setIsInitialLoad(false);
    } else if (!existingBoxAssociationIdFromFilter && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [existingBoxAssociationFromFilter?.boxId, existingBoxAssociationIdFromFilter, isInitialLoad]);

  // États pour la modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  
  // État pour l'accordéon
  const [isExpanded, setIsExpanded] = useState(false);

 

  const statusList: StatusItem[] = [
    {
      value: "WAITING_VALIDATION",
      label: "En attente de traitement / À traiter",
    },
    {
      value: "VALIDATE",
      label: "Validé",
    },
    {
      value: "SAVED",
      label: "Sauvegardé",
    },
    {
      value: "WAITING_UPDATE",
      label: "En attente de correction",
    },
    {
      value: "REFUSED",
      label: "Refusé",
    },
  ];

  const typeList: TypeItem[] = [
    {
      value: "PROJECT",
      label: "Projet",
    },
    {
      value: "BOX",
      label: "Box",
    },
    {
      value: "CURRENT",
      label: "Courant",
    },
  ];

  const convertFilter = (filter: FilterSpendingDemand) => {
    const result = { ...filter };

    if (filter.status && filter.status?.includes('WAITING_VALIDATION')) {
      filter.status = ['WAITING_VALIDATION'];
      result.managerWaitingValidation = [userInfo?.user_employee_id || ""];
    }

    return result;
  };

  const handleClickStatus = (valueClicked: string) => {
    if (valueClicked) {
      const currentStatus = filter?.status || [];

      if (filter?.status?.includes(valueClicked)) {
        setFilter(convertFilter({
          ...filter,
          status: currentStatus.filter((status) => status !== valueClicked),
        }));
      } else {
        setFilter(convertFilter({ ...filter, status: [...currentStatus, valueClicked] }));
      }
    }
  };

  const handleClickType = (valueClicked: string) => {
    if (valueClicked) {
      const currentType = filter?.type || [];

      if (filter?.type?.includes(valueClicked as SpendingDemandTypes)) {
        setFilter({
          ...filter,
          type: currentType.filter((type) => type !== valueClicked),
        });
      } else {
        setFilter({ ...filter, type: [...currentType, valueClicked as SpendingDemandTypes] });
      }
    }
  };

  const handleDateChange = (field: 'min' | 'max', value: string) => {
    const newDateRange = { ...dateRange, [field]: value };
    setDateRange(newDateRange);

    const dateFilter = {
      min: newDateRange.min || undefined,
      max: newDateRange.max || undefined,
    };

    setFilter({
      ...filter,
      date: dateFilter,
    });
  };

  const handleAmountChange = (field: 'min' | 'max', value: number | '') => {
    const newAmountRange = { ...amountRange, [field]: value };
    setAmountRange(newAmountRange);

    const amountFilter = {
      min: newAmountRange.min === '' ? undefined : newAmountRange.min as number,
      max: newAmountRange.max === '' ? undefined : newAmountRange.max as number,
    };

    setFilter({
      ...filter,
      amountTTC: amountFilter,
    });
  };

  const handleProjectChange = (projectId: string) => {
    setSelectedProjectId(projectId || null);
    
    const projectFilter = projectId ? [projectId] : undefined;
    
    setFilter({
      ...filter,
      projectId: projectFilter,
    });
  };

  const handleProjectSelect = (project: any) => {
    setSelectedProjectId(project.project_id);
    setSelectedProjectName(project.project_name);
    setIsProjectModalOpen(false);
    
    setFilter({
      ...filter,
      projectId: [project.project_id],
    });
  };

  const handleOpenProjectModal = () => {
    setIsProjectModalOpen(true);
  };

  const handleCloseProjectModal = () => {
    setIsProjectModalOpen(false);
  };

  const handleUserChange = (userId: string) => {
    setSelectedUserId(userId || null);
    
    const userFilter = userId ? [userId] : undefined;
    
    setFilter({
      ...filter,
      userId: userFilter,
    });
  };

  const handleUserSelect = (user: any) => {
    setSelectedUserId(user.user_id);
    setSelectedUserName(`${user.username} ${user.lastname}`);
    setIsUserModalOpen(false);
    
    setFilter({
      ...filter,
      userId: [user.user_id],
    });
  };

  const handleOpenUserModal = () => {
    setIsUserModalOpen(true);
  };

  const handleCloseUserModal = () => {
    setIsUserModalOpen(false);
  };

  const handleBoxIdChange = (value: string) => {
    setBoxIdInput(value);
    // Si le champ est vidé, retirer le filtre immédiatement
    if (!value || value.trim() === '') {
      setFilter({
        ...filter,
        boxAssociationId: undefined,
      });
    }
  };

  const handleReadableIdChange = (value: string) => {
    setReadableIdInput(value);
    setFilter({
      ...filter,
      readableId: value && value.trim() !== '' ? value.trim() : undefined,
    });
  };

  const handleClearFilters = () => {
    setDateRange({ min: '', max: '' });
    setAmountRange({ min: '', max: '' });
    setSelectedProjectId(null);
    setSelectedProjectName(null);
    setSelectedUserId(null);
    setSelectedUserName(null);
    setBoxIdInput('');
    setReadableIdInput('');
    setDescriptionInput('');
    setFilter({
      ...filter,
      status: [],
      type: [],
      projectId: undefined,
      userId: undefined,
      boxAssociationId: undefined,
      readableId: undefined,
      description: undefined,
      date: { min: undefined, max: undefined },
      amountTTC: { min: undefined, max: undefined },
    });
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleToggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const handleApplyFilters = () => {
    // Appliquer les filtres et fermer la modal
    setIsModalOpen(false);
  };

  return (
    <Box>
      {/* Modal de filtres */}
      <SpendingDemandListFilterView
        handleClickStatus={handleClickStatus}
        handleClickType={handleClickType}
        handleDateChange={handleDateChange}
        handleAmountChange={handleAmountChange}
        handleProjectChange={handleProjectChange}
        handleOpenProjectModal={handleOpenProjectModal}
        handleUserChange={handleUserChange}
        handleOpenUserModal={handleOpenUserModal}
        handleBoxIdChange={handleBoxIdChange}
        handleReadableIdChange={handleReadableIdChange}
        handleDescriptionChange={handleDescriptionChange}
        handleClearFilters={handleClearFilters}
        handleCloseModal={handleCloseModal}
        handleApplyFilters={handleApplyFilters}
        isModalOpen={isModalOpen}
        statusList={statusList}
        typeList={typeList}
        currentStatusSelected={filter?.status || []}
        currentTypeSelected={filter?.type || []}
        currentProjectSelected={selectedProjectId}
        selectedProjectName={selectedProjectName}
        currentUserSelected={selectedUserId}
        selectedUserName={selectedUserName}
        boxIdInput={boxIdInput}
        boxIdValidationStatus={{
          isValid: !!boxAssociationFromBoxId?.id,
          isLoading: isValidatingBox,
          isError: isBoxError,
        }}
        readableIdInput={readableIdInput}
        descriptionInput={descriptionInput}
        dateRange={dateRange}
        amountRange={amountRange}
        isExpanded={isExpanded}
        onToggleExpanded={handleToggleExpanded}
        forUser={forUser}
      />

      {/* Modal de sélection de projet */}
      <Dialog
        open={isProjectModalOpen}
        onClose={handleCloseProjectModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            backgroundColor: '#ffffff',
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 2,
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: '1px solid #e0e0e0'
        }}>
          <Typography variant="h6" component="h2" sx={{ 
            fontWeight: 500,
            color: '#333333'
          }}>
            Sélectionner un projet
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ height: '400px' }}>
            <SearchPickProject
              setProject={handleProjectSelect}
              heightListResult="350px"
              scrollable={true}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 1, borderTop: '1px solid #e0e0e0', justifyContent: 'flex-end' }}>
          <Button 
            onClick={handleCloseProjectModal}
            variant="outlined"
            sx={{ 
              borderColor: '#cccccc',
              color: '#666666',
              '&:hover': {
                borderColor: '#999999',
                backgroundColor: '#f5f5f5'
              }
            }}
          >
            Annuler
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de sélection d'utilisateur */}
      <Dialog
        open={isUserModalOpen}
        onClose={handleCloseUserModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            backgroundColor: '#ffffff',
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 2,
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: '1px solid #e0e0e0'
        }}>
          <Typography variant="h6" component="h2" sx={{ 
            fontWeight: 500,
            color: '#333333'
          }}>
            Sélectionner un utilisateur
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ height: '400px' }}>
            <SearchPickUser
              onClickEmployeeFound={handleUserSelect}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 1, borderTop: '1px solid #e0e0e0', justifyContent: 'flex-end' }}>
          <Button 
            onClick={handleCloseUserModal}
            variant="outlined"
            sx={{ 
              borderColor: '#cccccc',
              color: '#666666',
              '&:hover': {
                borderColor: '#999999',
                backgroundColor: '#f5f5f5'
              }
            }}
          >
            Annuler
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
